import React, { useMemo, useState, useEffect } from 'react';
import { Row, Col, Button } from 'reactstrap';

import ControldocDataTable from '../../../../controldoc/datatable';
import { aasmStateBadge, riskLevelBadge } from '../../../helper';

const ComplianceBulkLoadActivityItemTable = (props) => {
  const {
    bulkLoadActivityId,
    complianceContextId,
    data: {
      aasmStateOptions,
      customFields = [],
    },
  } = props;

  const defaultRequestParams = { compliance_context_id: complianceContextId, bulk_load_activity_id: bulkLoadActivityId }

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);

  useEffect(() => {
    if (tableColumns) {
      let visibleColumns = _.reject(tableColumns, {'hidden': true});

      setSelectedColumns(visibleColumns);
      setSelectedColumnKeys( _.map(visibleColumns, 'field') );
    }
  }, []);

  const onColumnSelectionChange = (e) => {
    const newSelectedColumnKeys = e.value;
    const filteredColumns = _.filter(tableColumns, (column) => _.includes(newSelectedColumnKeys, column.field));

    setSelectedColumns(filteredColumns);
    setSelectedColumnKeys(newSelectedColumnKeys);
  };

  const tableColumnsDefault = [
    {
      field: 'name',
      header: I18n.t('activerecord.attributes.compliance/activity.name'),
      body: (row) => row?.raw_data?.name || I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: 'Ingrese el nombre de la actividad',
        filterrific: { search: 'search_by_name_key_raw_data' }
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'name_' }
      }
    },
    {
      field: 'description',
      header: I18n.t('activerecord.attributes.compliance/activity.description'),
      body: (row) => _.truncate(row?.raw_data?.description, { length: 50 }) || I18n.t('no_entry'),
      hidden: true,
      filter: {
        enabled: true,
        placeholder: 'Ingrese la descripción de la actividad',
        filterrific: { search: 'search_by_description_key_raw_data' }
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'description_' }
      }
    },
    {
      field: 'recurrence',
      header: I18n.t('activerecord.attributes.compliance/activity.recurrence.one'),
      body: (row) => row?.raw_data?.recurrence || I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: 'Ingrese la recurrencia de la actividad',
        filterrific: { search: 'search_by_recurrence_key_raw_data' }
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'recurrence_' }
      }
    },
    {
      field: 'start_date',
      header: I18n.t('activerecord.attributes.compliance/activity.start_date'),
      body: (row) => moment(row?.raw_data?.start_date).isValid() ? moment(row?.raw_data?.start_date).format('DD-MM-YYYY') : I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: 'Ingrese una fecha de inicio',
        filterrific: { search: 'search_by_start_date_key_raw_data' },
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'start_date_' }
      }
    },
    {
      field: 'deadline',
      header: I18n.t('activerecord.attributes.compliance/activity.deadline'),
      body: (row) => moment(row?.raw_data?.deadline).isValid() ? moment(row?.raw_data?.deadline).format('DD-MM-YYYY') : I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: 'Ingrese una fecha de cumplimiento',
        filterrific: { search: 'search_by_deadline_key_raw_data' },
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'deadline_' }
      }
    },
    {
      field: 'uploader_groups',
      header: I18n.t('activerecord.attributes.compliance/activity.uploader_groups'),
      body: (row) => renderList(row?.raw_data?.uploader_groups),
      hidden: true,
      filter: {
        enabled: true,
        placeholder: 'Ingrese los grupos de uploaders',
        filterrific: { search: 'search_by_uploader_groups_key_raw_data' }
      },
    },
    {
      field: 'reviewer_groups',
      header: I18n.t('activerecord.attributes.compliance/activity.reviewer_groups'),
      body: (row) => renderList(row?.raw_data?.reviewer_groups),
      hidden: true,
      filter: {
        enabled: true,
        placeholder: 'Ingrese los grupos de reviewers',
        filterrific: { search: 'search_by_reviewer_groups_key_raw_data' }
      },
    },
    {
      field: 'consultant_groups',
      header: I18n.t('activerecord.attributes.compliance/activity.consultant_groups'),
      body: (row) => renderList(row?.raw_data?.consultant_groups),
      hidden: true,
      filter: {
        enabled: true,
        placeholder: 'Ingrese los grupos de consultants',
        filterrific: { search: 'search_by_consultant_groups_key_raw_data' }
      },
    },
    {
      field: 'informer_groups',
      header: I18n.t('activerecord.attributes.compliance/activity.informer_groups'),
      body: (row) => renderList(row?.raw_data?.informer_groups),
      hidden: true,
      filter: {
        enabled: true,
        placeholder: 'Ingrese los grupos de informers',
        filterrific: { search: 'search_by_informer_groups_key_raw_data' }
      },
    },
    {
      field: 'aasm_state',
      header: I18n.t('activerecord.attributes.compliance/activity.aasm_state.one'),
      body: (row) => row?.compliance_activity ? aasmStateBadge(row?.compliance_activity?.aasm_state, 'compliance/activity') : row?.raw_data?.aasm_state || I18n.t('no_entry'),
      filter: {
        enabled: true,
        inputType: 'simple_select',
        placeholder: 'Selecciona un estado',
        options: aasmStateOptions,
        filterrific: { search: 'by_aasm_state' }
      }
    },
    {
      field: 'risk_level',
      header: I18n.t('activerecord.models.compliance/risk_level.one'),
      body: (row) => row?.compliance_activity ? riskLevelBadge(row?.compliance_activity?.risk_level) : _.capitalize(row?.raw_data?.compliance_risk_level_id) || I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: 'Selecciona un nivel de riesgo',
        filterrific: { search: 'search_by_compliance_risk_level_id_key_raw_data' }
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'risk_level_' }
      }
    },
    {
      field: 'evidences',
      header: I18n.t('activerecord.models.compliance/evidence.other'),
      body: (row) => renderList(row?.raw_data?.evidences),
      hidden: true,
      filter: {
        enabled: true,
        placeholder: 'Ingrese las evidencias',
        filterrific: { search: 'search_by_evidences_key_raw_data' }
      },
    },
  ];

  const renderList = (items) => {
    if (_.isEmpty(items)) { return I18n.t('no_entry'); }

    return (
      <ul className="pl-3">
        {_.map(_.split(items, ', '), (item) => (
          <li key={item} className="small">{item}</li>
        ))}
      </ul>
    )
  }

  const tableColumnsCustomFields = useMemo(() => {
    return _.map(customFields, (customField) => ({
      field: `custom_fields.${customField.code}`,
      hidden: true,
      header: _.capitalize(customField.label),
      body: (row) => row?.raw_data?.custom_fields?.[customField.code] || I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: `Ingrese ${_.capitalize(customField.label)}`,
        filterrific: {
          search: 'search_by_key_custom_fields',
          searchKey: customField.code
        }
      }
    }))
  }, [customFields])

  const tableColumnsDetails = [
    {
      field: 'details',
      header: I18n.t('activerecord.attributes.compliance/bulk/load/activity.details'),
      body: (row) =>
      <>
        {
          row?.compliance_activity?.policy?.show && (
            <Button color="primary" outline size="sm" className="ml-2" href={Routes.compliance_activity_path(row?.compliance_activity_id, { compliance_context_id: complianceContextId })}>
              {I18n.t('actions.show')}
            </Button>
          ) || (
            renderList(row?.bulk_errors?.message?.[0])
          )
        }
      </>
    }
  ];

  const tableColumns = useMemo(() => {
    let columns = _.concat(
      tableColumnsDefault,
      tableColumnsCustomFields,
      tableColumnsDetails
    )

    return _.compact(columns)
  }, [tableColumnsCustomFields])

  return (
    <Row>
      <Col md="12">
        <ControldocDataTable
          columns={selectedColumns}
          configuration={{
            showDistributionListFilter: false,
            showGridlines: true,
            context: 'compliance',
            fetchRecords: {
              endpoint: Routes.compliance_bulk_load_activity_items_path,
              keyName: 'items',
              defaultRequestParams: defaultRequestParams
            },
            columnSelectable: {
              enabled: true,
              columns: tableColumns,
              selection: selectedColumnKeys,
              onSelectionChange: onColumnSelectionChange,
            }
          }}
        />
      </Col>
    </Row>
  );
};

export default ComplianceBulkLoadActivityItemTable;